import type {
  BodyTheme,
  ButtonsTheme,
  FormInputTheme,
  HeadingsTheme,
  HyperlinkTheme,
} from "../types/theme";
import { mutedInkOn, surfaceOrPage } from "./inkLegibility";

type Palette = {
  primary: string;
  secondary: string;
  background: string;
};

type Fonts = {
  header: string;
  body: string;
};

type ResolvedButtonStateTheme = {
  bgColor: string;
  bgHoverColor: string;
  borderColor: string;
  borderHoverColor: string;
  fontColor: string;
  fontHoverColor: string;
  fontSize: string;
  fontHoverSize: string;
  borderRadius: string;
  hoverBorderRadius: string;
};

type ResolvedFormInputTheme = {
  labelFontColor: string;
  inputFontColor: string;
  backgroundColor: string;
  border: boolean;
  borderColor: string;
  borderRadius: string;
  borderStyle: string;
  borderWidth: string;
  borderSides: string[];
  fontFamily: string;
  fontSize: string;
  errorColor: string;
};

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === "string" && value.trim().length > 0;

const firstNonEmptyString = (
  ...values: Array<string | null | undefined>
): string | undefined => {
  for (const value of values) {
    if (isNonEmptyString(value)) return value.trim();
  }
  return undefined;
};

export const resolveHeadingFontFamily = ({
  override,
  level,
  headingsTheme,
  fonts,
}: {
  override?: string | undefined;
  level: "h1" | "h2";
  headingsTheme?: HeadingsTheme | undefined;
  fonts: Fonts;
}) =>
  firstNonEmptyString(override, headingsTheme?.[level]?.fontFamily, fonts.header)
  ?? fonts.header;

export const resolveHeadingColor = ({
  override,
  level,
  headingsTheme,
  palette,
}: {
  override?: string | undefined;
  level: "h1" | "h2";
  headingsTheme?: HeadingsTheme | undefined;
  palette: Palette;
}) =>
  firstNonEmptyString(override, headingsTheme?.[level]?.fontColor, palette.primary)
  ?? palette.primary;

export const resolveHeadingFontSize = ({
  override,
  level,
  headingsTheme,
}: {
  override?: string | undefined;
  level: "h1" | "h2";
  headingsTheme?: HeadingsTheme | undefined;
}) =>
  firstNonEmptyString(
    override,
    headingsTheme?.[level]?.fontSize,
    level === "h1" ? "36px" : "22px",
  ) ?? (level === "h1" ? "36px" : "22px");

export const resolveBodyColor = ({
  override,
  bodyTheme,
  palette,
}: {
  override?: string | undefined;
  bodyTheme?: BodyTheme | undefined;
  // Only the surface is read — the legal line has no palette to hand over.
  palette: Pick<Palette, "background">;
}) => {
  // The fallback is NOT palette.secondary: that is the decorative accent, and
  // sites created before the cockpit persisted theme colours store nothing
  // here, so every one of them rendered body copy in the accent (charity
  // 1.51:1). A stored value and an explicit override still win untouched —
  // this only changes what happens when the column is empty. See
  // inkLegibility.ts.
  const stored = firstNonEmptyString(override, bodyTheme?.fontColor);
  return stored ?? mutedInkOn(palette.background);
};

export const resolveBodyFontFamily = ({
  override,
  fonts,
}: {
  override?: string | undefined;
  fonts: Fonts;
}) => firstNonEmptyString(override, fonts.body) ?? fonts.body;

export const resolveBodyFontSize = ({
  override,
  bodyTheme,
}: {
  override?: string | undefined;
  bodyTheme?: BodyTheme | undefined;
}) => firstNonEmptyString(override, bodyTheme?.fontSize, "16px") ?? "16px";

export const resolveHyperlinkFontColor = ({
  override,
  hyperlinkTheme,
  palette,
  allowInherit,
}: {
  override?: string | undefined;
  hyperlinkTheme?: HyperlinkTheme | undefined;
  palette: Palette;
  allowInherit: boolean;
}) => {
  const resolved =
    firstNonEmptyString(override, hyperlinkTheme?.fontColor)
    ?? (allowInherit ? "inherit" : palette.secondary);

  if (resolved === "inherit" && !allowInherit) return palette.secondary;
  return resolved;
};

export const resolveHyperlinkHoverColor = ({
  override,
  hyperlinkTheme,
  palette,
}: {
  override?: string | undefined;
  hyperlinkTheme?: HyperlinkTheme | undefined;
  palette: Palette;
}) =>
  firstNonEmptyString(override, hyperlinkTheme?.fontHoverColor, palette.primary)
  ?? palette.primary;

export const resolveHyperlinkUnderline = (hyperlinkTheme?: HyperlinkTheme | undefined) =>
  hyperlinkTheme?.underline ?? false;

export const resolveButtonThemeForType = ({
  type,
  buttonsTheme,
  palette,
  currentRounding,
}: {
  type: "primary" | "secondary";
  buttonsTheme?: ButtonsTheme | undefined;
  palette: Palette;
  currentRounding: string;
}): ResolvedButtonStateTheme => {
  const buttonTheme = buttonsTheme?.[type] ?? {};
  const bgColor =
    firstNonEmptyString(
      buttonTheme.bgColor,
      type === "primary" ? palette.primary : "transparent",
    ) ?? (type === "primary" ? palette.primary : "transparent");
  const borderColor =
    firstNonEmptyString(
      buttonTheme.borderColor,
      type === "primary" ? palette.primary : palette.secondary,
    ) ?? (type === "primary" ? palette.primary : palette.secondary);
  // `bgColor` above can be undefined when the palette is partial, and this line
  // runs for BOTH button types before the branch — the same shape as the
  // undefined-surface crash this file already caused once.
  const ghostSurface = surfaceOrPage(bgColor, palette.background);
  const fontColor =
    firstNonEmptyString(
      buttonTheme.fontColor,
      type === "primary" ? palette.background : undefined,
    ) ?? (type === "primary" ? palette.background : mutedInkOn(ghostSurface));
  const fontSize = firstNonEmptyString(buttonTheme.fontSize, "16px") ?? "16px";
  const borderRadius =
    firstNonEmptyString(buttonTheme.borderRadius, currentRounding, "rounded-lg")
    ?? "rounded-lg";

  return {
    bgColor,
    bgHoverColor: firstNonEmptyString(buttonTheme.bgHoverColor, bgColor) ?? bgColor,
    borderColor,
    borderHoverColor:
      firstNonEmptyString(buttonTheme.borderHoverColor, borderColor) ?? borderColor,
    fontColor,
    fontHoverColor: firstNonEmptyString(buttonTheme.fontHoverColor, fontColor) ?? fontColor,
    fontSize,
    fontHoverSize: firstNonEmptyString(buttonTheme.fontHoverSize, fontSize) ?? fontSize,
    borderRadius,
    hoverBorderRadius:
      firstNonEmptyString(buttonTheme.hoverBorderRadius, borderRadius) ?? borderRadius,
  };
};

export const resolveFormInputTheme = ({
  formInputTheme,
  palette,
  fonts,
  currentRounding,
}: {
  formInputTheme?: FormInputTheme | undefined;
  palette: Palette;
  fonts: Fonts;
  currentRounding: string;
}): ResolvedFormInputTheme => ({
  // The LABEL sits on the page; the value sits on the input's own fill. Using
  // one surface for both is wrong for whichever one it is not — and on a dark
  // site it was a strict worsening (6.53:1 → 2.82:1) because `transparent`
  // and `inherit` are legal fills that read as white to the contrast maths.
  labelFontColor:
    firstNonEmptyString(formInputTheme?.labelFontColor) ?? mutedInkOn(palette.background),
  inputFontColor:
    firstNonEmptyString(formInputTheme?.inputFontColor)
    ?? mutedInkOn(surfaceOrPage(formInputTheme?.backgroundColor, palette.background)),
  backgroundColor:
    firstNonEmptyString(formInputTheme?.backgroundColor, palette.background)
    ?? palette.background,
  border: formInputTheme?.border ?? true,
  borderColor:
    firstNonEmptyString(formInputTheme?.borderColor, palette.secondary) ?? palette.secondary,
  borderRadius:
    firstNonEmptyString(formInputTheme?.borderRadius, currentRounding, "rounded-md")
    ?? "rounded-md",
  borderStyle:
    firstNonEmptyString(formInputTheme?.borderStyle, "border-solid") ?? "border-solid",
  borderWidth: firstNonEmptyString(formInputTheme?.borderWidth, "border") ?? "border",
  borderSides: Array.isArray(formInputTheme?.borderSides)
    ? formInputTheme.borderSides
    : [],
  fontFamily: firstNonEmptyString(formInputTheme?.fontFamily, fonts.body) ?? fonts.body,
  fontSize: firstNonEmptyString(formInputTheme?.fontSize, "text-sm") ?? "text-sm",
  errorColor: firstNonEmptyString(formInputTheme?.errorColor, "#ef4444") ?? "#ef4444",
});
